import { type FC, useCallback, useState } from "react";

import { Tab } from "@snapchat/snap-design-system";

import { MetricField } from "../../../gql-generated/types";
import {
    useInsightsPageBlizzardEvent,
    useLensIdSearchParameter,
    useOrganicStatsTotal,
    useSubscribersTotal,
    useTimeRange,
} from "../../hooks";
import { type InsightsMetricsMap, type StatsItem, audienceTabsMetricsMap } from "../../models";
import { ChartSkeletonContainer } from "../ChartSkeletonContainer";
import { InsightsSubTabs } from "../InsightsSubTabs";
import { SubTabContent } from "../SubTabContent";
import { SubTabHeader } from "../SubTabHeader";
import { BarChartComponent } from "../charts";

import { AudienceSection } from "./AudienceSection";

interface AudienceTabProps {
    chartsMetrics: InsightsMetricsMap<StatsItem>;
    loading: boolean;
}

export const AudienceTab: FC<AudienceTabProps> = ({ chartsMetrics, loading }) => {
    const { isAllTimeSelected } = useTimeRange();
    const { lensId } = useLensIdSearchParameter();
    const { totalMetrics } = useOrganicStatsTotal();
    const { subscribersTotalValues } = useSubscribersTotal();

    const [audienceSectionBaseMetric, setAudienceSectionBaseMetric] = useState(audienceTabsMetricsMap[0]);

    const shouldShowReach = !isAllTimeSelected;
    const shouldShowTotalReach = !isAllTimeSelected;
    const shouldShowSubscribers = !lensId;
    const shouldShowAudience = !isAllTimeSelected && audienceSectionBaseMetric;

    const handleTabSelected = useCallback((index: number) => {
        setAudienceSectionBaseMetric(audienceTabsMetricsMap[index]);
    }, []);

    useInsightsPageBlizzardEvent("MY_LENSES_INSIGHTS_AUDIENCE");

    return (
        <>
            <InsightsSubTabs onTabSelected={handleTabSelected}>
                {shouldShowTotalReach ? (
                    <Tab
                        title={
                            <SubTabHeader
                                label="Total Reach (Plays and Views)"
                                tooltip="The total number of users that have viewed or played with your Lens(es)."
                                statValue={totalMetrics?.[MetricField.LensViewers]?.totalValueFormatted ?? ""}
                                diffValue={totalMetrics?.[MetricField.LensViewers]?.diffValue}
                                loading={loading}
                            />
                        }
                    >
                        <SubTabContent>
                            <ChartSkeletonContainer skeletonActive={loading}>
                                {chartsMetrics[MetricField.LensViewers] && (
                                    <BarChartComponent
                                        data={chartsMetrics[MetricField.LensViewers]}
                                        contentName="Viewers"
                                    />
                                )}
                            </ChartSkeletonContainer>
                        </SubTabContent>
                    </Tab>
                ) : undefined}
                {shouldShowReach ? (
                    <Tab
                        title={
                            <SubTabHeader
                                label={"Reach (Plays Only)"}
                                tooltip="The total number of users that have played with your Lens(es)."
                                statValue={totalMetrics?.[MetricField.LensUniques]?.totalValueFormatted ?? ""}
                                diffValue={totalMetrics?.[MetricField.LensUniques]?.diffValue}
                                loading={loading}
                            />
                        }
                    >
                        <SubTabContent>
                            <ChartSkeletonContainer skeletonActive={loading}>
                                {chartsMetrics[MetricField.LensUniques] && (
                                    <BarChartComponent
                                        data={chartsMetrics[MetricField.LensUniques]}
                                        contentName="Viewers"
                                    />
                                )}
                            </ChartSkeletonContainer>
                        </SubTabContent>
                    </Tab>
                ) : undefined}
                {shouldShowSubscribers ? (
                    <Tab
                        title={
                            <SubTabHeader
                                label="Total Subscribers"
                                tooltip="The total number of users who have subscribed to your profile in Snapchat."
                                statValue={subscribersTotalValues.totalValue}
                                diffValue={subscribersTotalValues.diffValue}
                                loading={loading}
                            />
                        }
                    >
                        <SubTabContent>
                            <ChartSkeletonContainer skeletonActive={loading}>
                                {chartsMetrics[MetricField.Subscribers] && (
                                    <BarChartComponent
                                        data={chartsMetrics[MetricField.Subscribers]}
                                        contentName="Total Subscribers"
                                    />
                                )}
                            </ChartSkeletonContainer>
                        </SubTabContent>
                    </Tab>
                ) : undefined}
            </InsightsSubTabs>
            {shouldShowAudience && <AudienceSection baseMetricField={audienceSectionBaseMetric} />}
        </>
    );
};
